/* eslint-disable no-unused-vars */
import firebase from "firebase/app";

/**
 * Normalizes a willcall record from old status-based convention to new numeric convention.
 * Old records may be missing checkedin/cancelled fields and rely only on status.
 *
 * @param {Object} record - The willcall record to normalize
 * @returns {Object} - The normalized record with proper numeric fields
 */
function normalizeWillcallRecord(record) {
  const normalized = { ...record };
  const quantity = parseInt(record.quantity) || 0;

  // Ensure checkedin field exists and is correct
  if (normalized.checkedin === undefined || normalized.checkedin === null) {
    normalized.checkedin = 0;
  }

  // For old 'checkedin' status records, checkedin should equal quantity
  if (record.status === "checkedin" && normalized.checkedin === 0) {
    normalized.checkedin = quantity;
  }

  // For old 'cancelled' status records, cancelled should equal quantity
  if (
    record.status === "cancelled" &&
    (normalized.cancelled === undefined || normalized.cancelled === null)
  ) {
    normalized.cancelled = quantity;
  }

  // For old 'refunded' status records, cancelled should equal quantity
  if (
    record.status === "refunded" &&
    (normalized.cancelled === undefined || normalized.cancelled === null)
  ) {
    normalized.cancelled = quantity;
  }

  return normalized;
}

/**
 * Checks if a record needs migration to the new data convention.
 * Returns the fields that need to be updated in Firestore.
 *
 * @param {Object} record - The original record from Firestore
 * @returns {Object|null} - Fields to update, or null if no migration needed
 */
function getRecordMigrationUpdates(record) {
  const updates = {};
  const quantity = parseInt(record.quantity) || 0;

  // Check if checkedin field is missing for a checkedin status
  if (
    record.status === "checkedin" &&
    (!record.checkedin || record.checkedin === 0)
  ) {
    updates.checkedin = quantity;
  }

  // Check if cancelled field is missing for a cancelled status
  if (
    record.status === "cancelled" &&
    (record.cancelled === undefined || record.cancelled === null)
  ) {
    updates.cancelled = quantity;
  }

  // Check if cancelled field is missing for a refunded status
  if (
    record.status === "refunded" &&
    (record.cancelled === undefined || record.cancelled === null)
  ) {
    updates.cancelled = quantity;
  }

  return Object.keys(updates).length > 0 ? updates : null;
}

export const state = {
  eventSummarys: [],
  festivalEventSummarys: [],
  seriesEventSummarys: [],
  eventSummary: {
    id: null,
    performerId: null,
    performerName: "",
    name: "",
    description: "",
    venue: "",
    step: 1,
    stepsCompleted: 0,
    minPrice: 0,
    maxPrice: 0,
    startDate: new Date()
  },
  dateSummaries: [],
  dateSummary: {},
  willcall: [],
  waitlist: [],
  audience: [],
  checkedin: [],
  cancelled: []
};

var audienceRef = null;
var audienceListener = null;
var willcallListener = null;
var checkedinListener = null;
var cancelledListener = null;

export const mutations = {
  ADD_EVENT_SUMMARY(state, summary) {
    state.eventSummarys.push(summary);
  },
  SET_EVENT_SUMMARY(state, event) {
    state.eventSummary = event;
  },
  CLEAR_EVENT_SUMMARIES(state) {
    state.eventSummarys = [];
  },
  ADD_FESTIVAL_EVENT_SUMMARY(state, summary) {
    state.festivalEventSummarys.push(summary);
  },
  CLEAR_FESTIVAL_EVENT_SUMMARIES(state) {
    state.eventSummarys = [];
  },
  ADD_SERIES_EVENT_SUMMARY(state, summary) {
    state.seriesEventSummarys.push(summary);
  },
  CLEAR_SERIES_EVENT_SUMMARIES(state) {
    state.seriesEventSummarys = [];
  },
  SET_DATE_SUMMARIES(state, summaries) {
    state.dateSummaries = summaries;
  },
  SET_DATE_SUMMARY(state, summary) {
    state.dateSummary = summary;
  },
  SET_WILL_CALL(state, willcall) {
    const sortedwillcall = willcall.sort((a, b) => {
      if (a.lastName.toUpperCase() < b.lastName.toUpperCase()) {
        return -1;
      }
      if (a.lastName.toUpperCase() > b.lastName.toUpperCase()) {
        return 1;
      }
      return 0;
    });
    state.willcall = willcall;
  },
  CLEAR_CHECKEDIN(state) {
    state.checkedin = [];
  },
  CLEAR_CANCELLED(state) {
    state.cancelled = [];
  },
  SET_CHECKEDIN(state, checkedin) {
    const sortedcheckedin = checkedin.sort((a, b) => {
      if (a.lastName.toUpperCase() < b.lastName.toUpperCase()) {
        return -1;
      }
      if (a.lastName.toUpperCase() > b.lastName.toUpperCase()) {
        return 1;
      }
      return 0;
    });
    state.checkedin = checkedin;
  },
  SET_CANCELLED(state, cancelled) {
    const sortedcancelled = cancelled.sort((a, b) => {
      if (a.lastName.toUpperCase() < b.lastName.toUpperCase()) {
        return -1;
      }
      if (a.lastName.toUpperCase() > b.lastName.toUpperCase()) {
        return 1;
      }
      return 0;
    });
    state.cancelled = sortedcancelled;
  },
  SET_AUDIENCE(state, audience) {
    state.audience = audience;
  },
  SET_WAITLIST(state, waitlist) {
    state.waitlist = waitlist;
  }
};

export const actions = {
  async fetchEventSummary({ commit }, eventId) {
    var db = firebase.firestore();
    var audRef = db
      .collection("events")
      .doc(eventId)
      .collection("userData")
      .doc("audienceReport");
    var aud = await audRef.get();

    if (aud.exists) {
      console.log("Found the event summary.");
      commit("SET_EVENT_SUMMARY", aud.data());
    } else {
      console.log("Could not find event " + eventId);
    }
  },
  async fetchEventWaitlist({ commit }, eventId) {
    // TODO: Consider wiping all event stuff at the event level
    commit("SET_WAITLIST", []);
    console.log("Getting waitlist", eventId);
    var db = firebase.firestore();
    var waitRef = db
      .collection("events")
      .doc(eventId)
      .collection("userData")
      .doc("audienceReport")
      .collection("waitlist")
      .orderBy("position", "asc");
    var docs = await waitRef.get();

    if (!docs.empty) {
      commit(
        "SET_WAITLIST",
        docs.docs.map(doc => doc.data())
      );
    } else {
      commit("SET_WAITLIST", []);
      console.log("No waitlist for this event.");
    }
  },
  async fetchEventSummarys({ commit }, profileId) {
    var db = firebase.firestore();
    console.log("Getting event summarys", profileId);
    // Clear out state
    commit("CLEAR_EVENT_SUMMARIES");

    // Get undated drafts
    var draftRef = db
      .collection("events")
      .where("owners", "array-contains", profileId)
      .where("start", "==", null);
    var drafts = await draftRef.get();

    if (!drafts.empty) {
      console.log("Found drafts. Loading summarys");
      drafts.forEach(async event => {
        const eventData = event.data();
        // console.log("event", eventData);
        // firebase doesn't allow two equality filters. doing this the weird way.
        if (eventData.status != "deleted")
          commit("ADD_EVENT_SUMMARY", {
            showMenu: false,
            event: eventData,
            audienceReport: null
          });
      });
    }

    console.log("profile id", profileId);
    // Get dated summaries
    var docRef = db
      .collection("events")
      .where("owners", "array-contains", profileId)
      .where("start", "!=", null)
      .orderBy("start", "desc");
    var docs = await docRef.get();

    if (!docs.empty) {
      console.log("Found the events. Loading summarys");
      docs.forEach(async event => {
        const eventData = event.data();
        // console.log("event", eventData);
        if (eventData.status != "deleted") {
          const summaryRef = db
            .collection("events")
            .doc(eventData.id)
            .collection("userData")
            .doc("audienceReport");
          const summary = await summaryRef.get();
          // console.log("summary", summary);
          commit("ADD_EVENT_SUMMARY", {
            showMenu: false,
            event: eventData,
            audienceReport: summary.data()
          });
        }
      });
    } else {
      console.log("Could not find any events. Not loading summarys.");
    }
  },
  async fetchFestivalEventSummarys({ commit }, seasonId) {
    var db = firebase.firestore();
    console.log("Getting festival event summarys", seasonId);
    // Clear out state
    commit("CLEAR_FESTIVAL_EVENT_SUMMARIES");

    // Get festival events
    var eventsRef = db
      .collection("events")
      .where("seasonId", "==", seasonId)
      .orderBy("title");
    var docs = await eventsRef.get();

    if (!docs.empty) {
      console.log("Found the events. Loading summarys");
      docs.forEach(async event => {
        const eventData = event.data();
        console.log("event", eventData);
        if (eventData.status != "deleted") {
          const summaryRef = db
            .collection("events")
            .doc(eventData.id)
            .collection("userData")
            .doc("audienceReport");
          const summary = await summaryRef.get();
          // console.log("summary", summary);
          commit("ADD_FESTIVAL_EVENT_SUMMARY", {
            showMenu: false,
            event: eventData,
            audienceReport: summary.data()
          });
        }
      });
    } else {
      console.log("Could not find any events. Not loading summarys.");
    }
  },
  async fetchSeriesEventSummarys({ commit }, seriesId) {
    var db = firebase.firestore();
    console.log("Getting series event summarys", seriesId);
    // Clear out state
    commit("CLEAR_FESTIVAL_EVENT_SUMMARIES");

    // Get festival events
    var eventsRef = db
      .collection("events")
      .where("seriesId", "==", seriesId)
      .orderBy("title");
    var docs = await eventsRef.get();

    if (!docs.empty) {
      console.log("Found the events. Loading summarys");
      docs.forEach(async event => {
        const eventData = event.data();
        console.log("event", eventData);
        if (eventData.status != "deleted") {
          const summaryRef = db
            .collection("events")
            .doc(eventData.id)
            .collection("userData")
            .doc("audienceReport");
          const summary = await summaryRef.get();
          // console.log("summary", summary);
          commit("ADD_SERIES_EVENT_SUMMARY", {
            showMenu: false,
            event: eventData,
            audienceReport: summary.data()
          });
        }
      });
    } else {
      console.log("Could not find any series events. Not loading summarys.");
    }
  },
  async fetchDateSummaries({ commit }, eventId) {
    var db = firebase.firestore();
    console.log("Getting date summarys", eventId);
    var docRef = db
      .collection("events")
      .doc(eventId)
      .collection("userData")
      .doc("audienceReport")
      .collection("dates")
      .orderBy("start"); // profileId);
    var docs = await docRef.get();

    if (!docs.empty) {
      commit(
        "SET_DATE_SUMMARIES",
        docs.docs.map(doc => doc.data())
      );
    } else {
      console.log("Could not find any date summaries.");
    }
  },
  async fetchDateSummary({ commit }, request) {
    var db = firebase.firestore();
    console.log("Getting date summary", request.eventId, request.dateId);
    var docRef = db
      .collection("events")
      .doc(request.eventId)
      .collection("userData")
      .doc("audienceReport")
      .collection("dates")
      .doc(request.dateId);
    var doc = await docRef.get();

    if (doc.exists) {
      console.log("Found the date summary.");
      commit("SET_DATE_SUMMARY", doc.data());
    } else {
      console.log("Could not find event " + request.dateId);
    }
  },
  async fetchWillcall({ commit }, request) {
    var db = firebase.firestore();
    console.log("Getting will call list", request.eventId, request.dateId);
    // Fetch all records that have remaining tickets to check in
    // This includes: paid, partiallycheckedin, mixed, and any record where quantity > checkedin
    var docRef = db
      .collection("events")
      .doc(request.eventId)
      .collection("userData")
      .doc("audienceReport")
      .collection("dates")
      .doc(request.dateId)
      .collection("audience")
      .where("status", "in", [
        "paid",
        "partiallycheckedin",
        "mixed",
        "checkedin",
        "cancelled",
        "refunded"
      ]);
    var docs = await docRef.get();

    // Filter to only records with remaining tickets to check in
    const filterWillcall = docs => {
      return docs
        .map(doc => {
          var ed = doc.data();
          if (!ed.id) ed.id = doc.id;
          // Normalize old records to new convention
          return normalizeWillcallRecord(ed);
        })
        .filter(record => {
          const quantity = record.quantity || 0;
          const checkedin = record.checkedin || 0;
          const remaining = quantity - checkedin;
          return remaining > 0;
        });
    };

    if (!docs.empty) {
      commit("SET_WILL_CALL", filterWillcall(docs.docs));
      willcallListener = docRef.onSnapshot(function(snapshot) {
        let update = snapshot.docs;
        console.log("will call list updated", update);
        commit("SET_WILL_CALL", filterWillcall(update));
      });
    } else {
      commit("SET_WILL_CALL", []);
      console.log("Could not find will call list.");
    }
  },
  async checkin({ dispatch }, request) {
    var db = firebase.firestore();
    console.log("Checking patron in", request.audience, request.checkqty);

    var docRef = await db
      .collection("events")
      .doc(request.eventId)
      .collection("userData")
      .doc("audienceReport")
      .collection("dates")
      .doc(request.dateId)
      .collection("audience")
      .doc(request.audience.id);
    //var docs = await docRef.get();
    var totalchecked = request.audience.checkedin + request.checkqty;
    if (totalchecked > request.audience.quantity)
      totalchecked = request.audience.quantity;
    var remaining = request.audience.quantity - totalchecked;
    if (remaining < 0) remaining = 0;

    // Determine status based on check-in state AND cancellation state
    var hasCancellations =
      request.audience.cancelled && request.audience.cancelled > 0;
    var status;

    if (remaining == 0 && !hasCancellations) {
      // All tickets checked in, no cancellations
      status = "checkedin";
    } else if (remaining == 0 && hasCancellations) {
      // All remaining tickets checked in, but has cancellations
      status = "mixed";
    } else if (remaining > 0) {
      // Still has tickets to check in
      status = hasCancellations ? "mixed" : "partiallycheckedin";
    }

    await docRef.update({ status: status, checkedin: totalchecked });
  },
  async undoCheckin({ dispatch }, request) {
    var db = firebase.firestore();
    console.log("Undoing operation for patron", request.audienceId);

    var docRef = db
      .collection("events")
      .doc(request.eventId)
      .collection("userData")
      .doc("audienceReport")
      .collection("dates")
      .doc(request.dateId)
      .collection("audience")
      .doc(request.audienceId);

    // Get current record to determine what to undo
    const doc = await docRef.get();
    const rawData = doc.data();

    // Normalize old records to new convention before processing
    const currentData = normalizeWillcallRecord(rawData);

    // Check if this old record needs migration fields added
    const migrationUpdates = getRecordMigrationUpdates(rawData);

    if (
      currentData.status === "cancelled" ||
      currentData.status === "refunded" ||
      currentData.status === "mixed"
    ) {
      // Undoing ALL cancellations - restore all cancelled tickets at once
      const cancelledCount = currentData.cancelled || 0;

      if (cancelledCount > 0) {
        // Restore all cancelled tickets
        const newQuantity = currentData.quantity + cancelledCount;
        const checkedinCount = currentData.checkedin || 0;

        let newStatus;
        if (checkedinCount === 0) {
          // No check-ins, back to paid
          newStatus = "paid";
        } else if (checkedinCount >= newQuantity) {
          // All tickets checked in
          newStatus = "checkedin";
        } else {
          // Some tickets checked in
          newStatus = "partiallycheckedin";
        }

        const updateData = {
          status: newStatus,
          quantity: newQuantity,
          cancelled: firebase.firestore.FieldValue.delete(),
          checkedin: checkedinCount
        };

        await docRef.update(updateData);
      }
    } else {
      // Undoing ALL check-ins - restore all checked-in tickets at once
      const hasCancellations =
        currentData.cancelled && currentData.cancelled > 0;
      const status = hasCancellations ? "mixed" : "paid";

      const updateData = { status: status, checkedin: 0 };

      // Include migration updates if needed (e.g., adding cancelled field)
      if (migrationUpdates) {
        Object.assign(updateData, migrationUpdates);
      }

      await docRef.update(updateData);
    }
  },
  async fetchCheckedIn({ commit }, request) {
    commit("CLEAR_CHECKEDIN");
    var db = firebase.firestore();
    console.log("Getting checkedin list", request.eventId, request.dateId);
    // Fetch all records that could have check-ins
    var docRef = db
      .collection("events")
      .doc(request.eventId)
      .collection("userData")
      .doc("audienceReport")
      .collection("dates")
      .doc(request.dateId)
      .collection("audience")
      .where("status", "in", [
        "paid",
        "partiallycheckedin",
        "mixed",
        "checkedin",
        "cancelled",
        "refunded"
      ]);
    var docs = await docRef.get();

    // Filter to only records with checkedin > 0
    const filterCheckedIn = docs => {
      return docs
        .map(doc => {
          var ed = doc.data();
          if (!ed.id) ed.id = doc.id;
          // Normalize old records to new convention
          return normalizeWillcallRecord(ed);
        })
        .filter(record => {
          const checkedin = record.checkedin || 0;
          return checkedin > 0;
        });
    };

    if (!docs.empty) {
      commit("SET_CHECKEDIN", filterCheckedIn(docs.docs));
    } else {
      commit("SET_CHECKEDIN", []);
      console.log("Could not find checkedin list.");
    }
    checkedinListener = docRef.onSnapshot(function(snapshot) {
      let update = snapshot.docs;
      console.log("checked in list updated", update);
      commit("SET_CHECKEDIN", filterCheckedIn(update));
    });
  },
  async fetchCancelled({ commit }, request) {
    commit("CLEAR_CANCELLED");
    var db = firebase.firestore();
    console.log("Getting cancelled list", request.eventId, request.dateId);
    // Fetch all records that could have cancellations
    var docRef = db
      .collection("events")
      .doc(request.eventId)
      .collection("userData")
      .doc("audienceReport")
      .collection("dates")
      .doc(request.dateId)
      .collection("audience")
      .where("status", "in", [
        "paid",
        "partiallycheckedin",
        "mixed",
        "checkedin",
        "cancelled",
        "refunded"
      ]);
    var docs = await docRef.get();

    // Filter to only records with cancelled > 0
    const filterCancelled = docs => {
      return docs
        .map(doc => {
          var ed = doc.data();
          if (!ed.id) ed.id = doc.id;
          // Normalize old records to new convention
          return normalizeWillcallRecord(ed);
        })
        .filter(record => {
          const cancelled = record.cancelled || 0;
          return cancelled > 0;
        });
    };

    if (!docs.empty) {
      commit("SET_CANCELLED", filterCancelled(docs.docs));
    } else {
      commit("SET_CANCELLED", []);
      console.log("Could not find cancelled list.");
    }
    cancelledListener = docRef.onSnapshot(function(snapshot) {
      let update = snapshot.docs;
      console.log("cancelled list updated", update);
      commit("SET_CANCELLED", filterCancelled(update));
    });
  },
  async fetchAudience({ commit }, request) {
    var db = firebase.firestore();
    console.log("Getting audience report", request.eventId, request.dateId);
    var docRef = db
      .collection("events")
      .doc(request.eventId)
      .collection("userData")
      .doc("audienceReport")
      .collection("dates")
      .doc(request.dateId)
      .collection("audience")
      .where("status", "in", [
        "checkedin",
        "partiallycheckedin",
        "paid",
        "mixed"
      ])
      .orderBy("lastName");
    var docs = await docRef.get();

    // Normalize records to handle legacy status-based data
    const normalizeAudience = docs => {
      return docs.map(doc => {
        var ed = doc.data();
        if (!ed.id) ed.id = doc.id;
        return normalizeWillcallRecord(ed);
      });
    };

    audienceRef = docRef;
    audienceListener = docRef.onSnapshot(function(snapshot) {
      let update = snapshot.docs;
      console.log("audience updated", update);
      commit("SET_AUDIENCE", normalizeAudience(update));
    });

    if (!docs.empty) {
      commit("SET_AUDIENCE", normalizeAudience(docs.docs));
    } else {
      commit("SET_AUDIENCE", []);
      console.log("Could not find audience for this event.");
    }
  },
  async fetchAudienceTemp({ commit }, request) {
    var db = firebase.firestore();
    console.log(
      "Getting audience report temp",
      request.eventId,
      request.dateId
    );
    var docRef = db
      .collection("events")
      .doc(request.eventId)
      .collection("userData")
      .doc("audienceReport")
      .collection("dates")
      .doc(request.dateId)
      .collection("audience")
      .where("status", "in", [
        "checkedin",
        "partiallycheckedin",
        "paid",
        "mixed"
      ])
      .orderBy("lastName");
    var docs = await docRef.get();

    audienceRef = docRef;
    audienceListener = docRef.onSnapshot(function(snapshot) {
      let update = snapshot.docs;
      console.log("audience updated", update);
      commit(
        "SET_AUDIENCE",
        update.map(doc => {
          var ed = doc.data();
          if (!ed.id) ed.id = doc.id;
          return normalizeWillcallRecord(ed);
        })
      );
    });

    if (!docs.empty) {
      const audience = [];
      const audiencedocs = docs.docs.map(doc => {
        var ed = doc.data();
        if (!ed.id) ed.id = doc.id;
        return normalizeWillcallRecord(ed);
      });
      console.log("adogs", audiencedocs.length);
      for (let x = 0; x < audiencedocs.length; x++) {
        let docdata = audiencedocs[x];
        let artistname = "";
        var orderitems = await db
          .collection("orders")
          .doc(docdata.orderId)
          .collection("lineItems")
          .get();
        orderitems.forEach(li => {
          let lidata = li.data();
          artistname = lidata.artistName;
          console.log("Artist name", lidata.artistName);
        });
        docdata.artistName = artistname;
        audience.push(docdata);
      }
      commit("SET_AUDIENCE", audience);
    } else {
      console.log("Could not find audience for this event.");
    }
  }
};

export const getters = {
  eventSummary(state) {
    return state.eventSummary;
  },
  eventSummarys(state) {
    return state.eventSummarys;
  },
  festivalEventSummarys(state) {
    return state.festivalEventSummarys;
  },
  seriesEventSummarys(state) {
    return state.seriesEventSummarys;
  },
  dateSummaries(state) {
    return state.dateSummaries;
  },
  dateSummary(state) {
    return state.dateSummary;
  },
  willcall(state) {
    return state.willcall;
  },
  audience(state) {
    return state.audience;
  },
  checkedin(state) {
    return state.checkedin;
  },
  cancelled(state) {
    return state.cancelled;
  },
  waitlist(state) {
    return state.waitlist;
  }
};
